import React, { useEffect, useState } from 'react'
import Button from '../../../../../components/ui/buttons/Button'
import CustomCheckbox from '../../../../../components/ui/checkboxes/CustomCheckbox'
import { fetchWithCsrf } from '../../../../../utils/fetchWithCsrf'
import { snakecaseKeys } from '../../../../../utils/snakecaseKeys'
import { camelizeKeys } from '../../../../../utils/camelizeKeys'
import PatrimonyFormList from '../../../../../components/forms/profilePatrimony/PatrimonyFormList'
import { useToast } from '../../../../../components/ui/toast/ToastContext'
import CurrencyInput from '../../../../../components/ui/inputs/CurrencyInput'
import { formatCurrency } from '../../../../../utils/formatters'

const AssetsAndProtectionsTab = ({ clientId, onSuccess, canWrite }) => {
  const [form, setForm] = useState({
    protections: [],
    patrimoniesAttributes: [],
    monthlySpend: '',
    monthlySavings: '',
    netTotal: '',
    netTotalFinancial: '',
    netTotalFixed: '',
  })
  const [protectionOptions, setProtectionOptions] = useState([])
  const [saving, setSaving] = useState(false)
  const { addToast } = useToast()

  const fetchData = () => {
    fetch(`/app/planner/client/${clientId}/profile/assets_and_protections`)
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        setForm(camelData)
      })
  }

  useEffect(() => {
    fetchData()
  }, [clientId])

  useEffect(() => {
    fetch('/app/shared/form_options')
      .then(res => res.json())
      .then(data => {
        const camelData = camelizeKeys(data)
        setProtectionOptions(camelData.protections.map(p => ({ label: p.label, value: p.value })))
      })
  }, [])

  const handleProtectionChange = (protectionType) => {
    setForm(prev => {
      const protections = prev.protections.includes(protectionType)
        ? prev.protections.filter(p => p !== protectionType)
        : [...prev.protections, protectionType]
      return { ...prev, protections }
    })
  }

  const handleSubmit = () => {
    setSaving(true)
    const payload = snakecaseKeys({ client: form })

    fetchWithCsrf(`/app/planner/client/${clientId}/profile/assets_and_protections`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (!res.ok) throw new Error()
        return res.json()
      })
      .then(() => {
        addToast({ type: 'success', message: 'Dados salvos com sucesso!' })
        fetchData()
        onSuccess()
      })
      .catch(() => {
        addToast({ type: 'error', message: 'Erro ao salvar dados.' })
      })
      .finally(() => setSaving(false))
  }

  return (
    <>
      <form onSubmit={(e) => {
        e.preventDefault()
        handleSubmit()
      }}>
        <div className="bg-white rounded-xl p-6 space-y-8">
          <div>
            <h2 className="text-lg font-semibold text-blueDarker mb-4">Informações Financeiras</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CurrencyInput
                name="monthlySpend"
                label="Quanto gasta por mês?"
                value={form.monthlySpend}
                required={true}
                onChange={(e) => setForm({ ...form, monthlySpend: e.target.value })}
                disabled={!canWrite}
              />
              <CurrencyInput
                name="monthlySavings"
                label="Quanto poupa por mês?"
                value={form.monthlySavings}
                required={true}
                onChange={(e) => setForm({ ...form, monthlySavings: e.target.value })}
                disabled={!canWrite}
              />
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-blueDarker mb-4">Proteções que já possui</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-4">
              {protectionOptions.map(({ label, value }) => (
                <CustomCheckbox
                  key={value}
                  name={value}
                  label={label}
                  labelClassName="text-base"
                  checked={form.protections.includes(value)}
                  onChange={() => handleProtectionChange(value)}
                  disabled={!canWrite}
                />
              ))}
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-blueDarker mb-4">Patrimônio e ativos</h2>
            <div className="px-44 mt-auto flex justify-between items-center mb-4">
              <div className="flex font-medium items-center gap-2">
                <span className="text-sm text-tealBase">PATRIMÔNIO TOTAL: </span><span className="text-xl blueDark">{formatCurrency(form.netTotal)}</span>
              </div>
              <div className="flex font-medium items-center gap-2">
                <span className="text-sm text-tealBase">PATRIMÔNIO FINANCEIRO: </span><span className="text-xl blueDark">{formatCurrency(form.netTotalFinancial)}</span>
              </div>
              <div className="flex font-medium items-center gap-2">
                <span className="text-sm text-tealBase">PATRIMÔNIO IMOBILIZADO: </span><span className="text-xl blueDark">{formatCurrency(form.netTotalFixed)}</span>
              </div>
            </div>
            <PatrimonyFormList
              patrimonies={form.patrimoniesAttributes}
              onChange={(updatedList) =>
                setForm(prev => ({ ...prev, patrimoniesAttributes: updatedList }))
              }
              canWrite={canWrite}
            />
          </div>
        </div>

        {canWrite && (
          <div className="flex justify-end mt-8">
            <Button type="submit" loading={saving}>
              SALVAR E CONTINUAR
            </Button>
          </div>
        )}
      </form>
    </>
  )
}

export default AssetsAndProtectionsTab
